import './list-zero-workflow.scss';

/*
 * Static documentation page describing how the daily List 0 submission is
 * processed into AD / DE / EC tickets (simplified flow, 2026-06).
 * Source of truth: endorsement repo, docs/business-rules-list-submit.md
 */

const COLORS = {
  decision: '#1d4ed8',
  decisionBg: '#eff6ff',
  outcome: '#475569',
  outcomeBg: '#f8fafc',
  list2: '#2d8a4e',
  list1: '#d9534f',
  waiting: '#f0ad4e',
  line: '#64748b',
};

type NodeProps = {
  x: number;
  y: number;
  w: number;
  h: number;
  lines: string[];
  variant?: 'decision' | 'outcome' | 'list1' | 'list2' | 'waiting' | 'start';
};

function Node({ x, y, w, h, lines, variant = 'outcome' }: NodeProps) {
  const styles: Record<string, { fill: string; stroke: string; text: string }> = {
    start: { fill: '#0f172a', stroke: '#0f172a', text: '#ffffff' },
    decision: { fill: COLORS.decisionBg, stroke: COLORS.decision, text: '#1e3a8a' },
    outcome: { fill: COLORS.outcomeBg, stroke: COLORS.outcome, text: '#334155' },
    list1: { fill: COLORS.list1, stroke: COLORS.list1, text: '#ffffff' },
    list2: { fill: COLORS.list2, stroke: COLORS.list2, text: '#ffffff' },
    waiting: { fill: COLORS.waiting, stroke: COLORS.waiting, text: '#ffffff' },
  };
  const s = styles[variant];
  const lineHeight = 15;
  const startY = y + h / 2 - ((lines.length - 1) * lineHeight) / 2;

  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={variant === 'decision' ? 28 : 8} fill={s.fill} stroke={s.stroke} strokeWidth={1.5} />
      {lines.map((line, i) => (
        <text key={i} x={x + w / 2} y={startY + i * lineHeight} textAnchor="middle" dominantBaseline="middle" fontSize={13.5} fontWeight={variant === 'decision' ? 600 : 500} fill={s.text}>
          {line}
        </text>
      ))}
    </g>
  );
}

type ArrowProps = { points: [number, number][]; label?: string; labelAt?: number };

function Arrow({ points, label, labelAt = 0 }: ArrowProps) {
  const d = points.map(([px, py], i) => `${i === 0 ? 'M' : 'L'} ${px} ${py}`).join(' ');
  const [lx, ly] = points[labelAt];

  return (
    <g>
      <path d={d} fill="none" stroke={COLORS.line} strokeWidth={1.5} markerEnd="url(#arrowhead)" />
      {label && (
        <text x={lx + 8} y={ly - 6} fontSize={12.5} fontWeight={700} fill={COLORS.line}>
          {label}
        </text>
      )}
    </g>
  );
}

function Defs() {
  return (
    <defs>
      <marker id="arrowhead" markerWidth="9" markerHeight="7" refX="8" refY="3.5" orient="auto">
        <polygon points="0 0, 9 3.5, 0 7" fill={COLORS.line} />
      </marker>
    </defs>
  );
}

function OverviewDiagram() {
  return (
    <svg viewBox="0 0 960 420" className="workflow-diagram" role="img" aria-label="List 0 processing overview">
      <Defs />
      <Node x={20} y={40} w={130} h={56} lines={['AZADEA', 'List 0 (daily)']} variant="start" />
      <Node x={210} y={40} w={170} h={56} lines={['Compare with yesterday', 'by PERSONID']} variant="outcome" />
      <Node x={440} y={40} w={140} h={56} lines={['Checking Zone', '(cancel add + delete)']} variant="outcome" />

      <Node x={640} y={10} w={130} h={40} lines={['Members To Add']} variant="decision" />
      <Node x={640} y={66} w={130} h={40} lines={['Members To Delete']} variant="decision" />
      <Node x={640} y={122} w={130} h={40} lines={['AZADEAID changes']} variant="decision" />

      <Node x={830} y={10} w={110} h={96} lines={['Scenario', 'routing']} variant="outcome" />
      <Node x={640} y={196} w={130} h={48} lines={['EC ticket', 'update AZADEAID']} variant="list2" />

      <Node x={400} y={280} w={180} h={52} lines={['List 1: error / waiting queue', '(missing data, blocked)']} variant="list1" />
      <Node x={660} y={280} w={180} h={52} lines={['List 2: clean tickets', 'waiting endorsement']} variant="list2" />

      <Node x={660} y={364} w={130} h={44} lines={['GLOBAL', '(insurer system)']} variant="start" />
      <Node x={850} y={364} w={100} h={44} lines={['List 3', 'insured']} variant="outcome" />

      <Arrow points={[[150, 68], [210, 68]]} />
      <Arrow points={[[380, 68], [440, 68]]} />
      <Arrow points={[[580, 55], [640, 30]]} />
      <Arrow points={[[580, 68], [640, 86]]} />
      <Arrow points={[[580, 81], [640, 142]]} />
      <Arrow points={[[770, 30], [830, 40]]} />
      <Arrow points={[[770, 86], [830, 76]]} />
      <Arrow points={[[705, 162], [705, 196]]} />
      <Arrow points={[[860, 106], [860, 250], [560, 250], [510, 280]]} label="problems" labelAt={2} />
      <Arrow points={[[885, 106], [885, 260], [760, 260], [752, 280]]} label="clean" labelAt={2} />
      <Arrow points={[[705, 244], [705, 264], [740, 264], [740, 280]]} />
      <Arrow points={[[490, 332], [490, 352], [640, 352], [660, 332]]} label="daily re-check, when unblocked" labelAt={1} />
      <Arrow points={[[727, 332], [727, 364]]} label="pull" />
      <Arrow points={[[790, 386], [850, 386]]} label="endorse" />
    </svg>
  );
}

function MembersToAddDiagram() {
  return (
    <svg viewBox="0 0 880 840" className="workflow-diagram workflow-diagram--tall" role="img" aria-label="Members To Add decision flow">
      <Defs />
      <Node x={330} y={10} w={240} h={40} lines={['Member To Add']} variant="start" />
      <Arrow points={[[450, 50], [450, 90]]} />

      <Node x={330} y={90} w={240} h={56} lines={['AD already', 'in List 1/2?']} variant="decision" />
      <Arrow points={[[570, 118], [640, 118]]} label="Yes" />
      <Node x={640} y={98} w={200} h={40} lines={['Ignore: already handled']} variant="outcome" />
      <Arrow points={[[450, 146], [450, 186]]} label="No" />

      <Node x={330} y={186} w={240} h={56} lines={['DE ticket exists', 'in List 1/2?']} variant="decision" />
      <Arrow points={[[330, 214], [150, 214], [150, 494], [330, 494]]} label="No" labelAt={0} />
      <Arrow points={[[450, 242], [450, 282]]} label="Yes" />

      <Node x={330} y={282} w={240} h={56} lines={['DE pulled by GLOBAL?', '(endorsement in progress)']} variant="decision" />
      <Arrow points={[[570, 310], [640, 310]]} label="Yes" />
      <Node x={640} y={282} w={210} h={56} lines={['List 1 · status 50', 'AD waits for the DE', 'to be processed']} variant="waiting" />
      <Arrow points={[[450, 338], [450, 378]]} label="No" />

      <Node x={330} y={378} w={240} h={48} lines={['Cancel the pending DE tickets', '(never endorsed)']} variant="outcome" />
      <Arrow points={[[450, 426], [450, 466]]} />

      <Node x={330} y={466} w={240} h={56} lines={['Member still in List 3?', '(insured, by AZADEAID)']} variant="decision" />
      <Arrow points={[[570, 494], [640, 494]]} label="Yes" />
      <Node x={640} y={474} w={200} h={40} lines={['Ignore: still insured']} variant="outcome" />
      <Arrow points={[[450, 522], [450, 562]]} label="No" />

      <Node x={330} y={562} w={240} h={48} lines={['Create the AD ticket']} variant="start" />
      <Arrow points={[[450, 610], [450, 650]]} />

      <Node x={330} y={650} w={240} h={56} lines={['Data clean and', 'policy found?']} variant="decision" />
      <Arrow points={[[390, 706], [180, 756]]} label="Yes" />
      <Node x={60} y={756} w={250} h={64} lines={['List 2: AD clean,', 'waiting endorsement', '(life and health)']} variant="list2" />
      <Arrow points={[[510, 706], [700, 756]]} label="No" />
      <Node x={590} y={756} w={250} h={64} lines={['List 1: missing information', '(status 3) or policy not found', '(status 13 / 14)']} variant="list1" />
    </svg>
  );
}

function MembersToDeleteDiagram() {
  return (
    <svg viewBox="0 0 880 846" className="workflow-diagram workflow-diagram--tall" role="img" aria-label="Members To Delete decision flow">
      <Defs />
      <Node x={330} y={10} w={240} h={40} lines={['Member To Delete']} variant="start" />
      <Arrow points={[[450, 50], [450, 90]]} />

      <Node x={330} y={90} w={240} h={56} lines={['Leave reason', 'is Transfer In?']} variant="decision" />
      <Arrow points={[[570, 118], [640, 118]]} label="Yes" />
      <Node x={640} y={98} w={210} h={40} lines={['Ignore: returning member']} variant="outcome" />
      <Arrow points={[[450, 146], [450, 186]]} label="No" />

      <Node x={330} y={186} w={240} h={56} lines={['DE already exists', 'in List 1/2?']} variant="decision" />
      <Arrow points={[[570, 214], [640, 214]]} label="Yes" />
      <Node x={640} y={194} w={210} h={40} lines={['Ignore: already leaving']} variant="outcome" />
      <Arrow points={[[450, 242], [450, 282]]} label="No" />

      <Node x={330} y={282} w={240} h={56} lines={['AD pulled by GLOBAL?', '(endorsement in progress)']} variant="decision" />
      <Arrow points={[[570, 310], [640, 310]]} label="Yes" />
      <Node x={640} y={282} w={210} h={56} lines={['List 1 · status 12', 'DE waits for the AD', 'to be processed']} variant="waiting" />
      <Arrow points={[[450, 338], [450, 378]]} label="No" />

      <Node x={330} y={378} w={240} h={48} lines={['Cancel any pending AD tickets', '(never endorsed)']} variant="outcome" />
      <Arrow points={[[450, 426], [450, 466]]} />

      <Node x={330} y={466} w={240} h={56} lines={['Member in List 3?', '(insured, by AZADEAID)']} variant="decision" />
      <Arrow points={[[570, 494], [640, 494]]} label="No" />
      <Node x={640} y={474} w={210} h={40} lines={['Ignore: was never insured']} variant="outcome" />
      <Arrow points={[[450, 522], [450, 562]]} label="Yes" />

      <Node x={330} y={562} w={240} h={56} lines={['Has leave date + reason,', 'and policy found?']} variant="decision" />
      <Arrow points={[[390, 618], [180, 668]]} label="Yes" />
      <Node x={60} y={668} w={250} h={64} lines={['List 2: DE clean,', 'waiting endorsement', '(life and health)']} variant="list2" />
      <Arrow points={[[510, 618], [700, 668]]} label="No" />
      <Node x={590} y={668} w={250} h={64} lines={['List 1: missing leave date /', 'leave reason (status 3) or', 'policy not found (13 / 14)']} variant="list1" />
    </svg>
  );
}

const DETECTION_RULES = [
  { test: 'Member in Today, not in Yesterday (no leave data)', result: 'Flag as Addition', kind: 'add' },
  { test: 'Member in Yesterday, not in Today', result: 'Flag as Deletion', kind: 'del' },
  { test: 'Member with leave reason and leave date ≤ today', result: 'Flag as Deletion', kind: 'del' },
  { test: 'Member with leave reason and leave date > today', result: 'Ignore (future departure)', kind: 'ignore' },
  { test: 'Member with leave reason but no leave date', result: 'Flag as Deletion', kind: 'del' },
  { test: 'Member with leave date ≤ today but no leave reason', result: 'Flag as Deletion', kind: 'del' },
];

const GLOBAL_STEPS = [
  'The system creates clean tickets directly in List 2, waiting for GLOBAL to pick them up.',
  'GLOBAL pulls the open List 2 tickets.',
  'GLOBAL marks each ticket it has taken. From this point the ticket is in progress with the insurer and can no longer be cancelled by a change in the daily list.',
  'GLOBAL endorses the member with the insurance company, or stops the endorsement. The insured list (List 3) is updated.',
  'GLOBAL updates the ticket status and issues the invoice.',
];

export default function ListZeroWorkflow() {
  return (
    <div className="list-zero-workflow">

      <section className="workflow-card">
        <h2>Overview</h2>
        <p>
          Every day AZADEA submits the full employee list (<strong>List 0</strong>). The system
          compares it with yesterday&apos;s list, identifies each member by{' '}
          <strong>PERSONID</strong>, and turns the differences into tickets:{' '}
          <strong>AD</strong> (add a member), <strong>DE</strong> (delete a member) and{' '}
          <strong>EC</strong> (update a member&apos;s AZADEAID). Clean tickets go directly to{' '}
          <strong>List 2</strong>, where the GLOBAL system picks them up. Tickets with problems or
          that must wait stay in <strong>List 1</strong>. Employees in Kingdom of Saudi Arabia are
          excluded, so no tickets are ever created for them.
        </p>
        <OverviewDiagram />
        <div className="workflow-legend">
          <span><i className="legend-swatch legend-swatch--list2" /> List 2: clean, ready for GLOBAL</span>
          <span><i className="legend-swatch legend-swatch--list1" /> List 1: needs data fix / review</span>
          <span><i className="legend-swatch legend-swatch--waiting" /> List 1: waiting for another ticket</span>
        </div>
      </section>

      <section className="workflow-card">
        <h2>Step 1: Detect changes</h2>
        <table className="workflow-table">
          <thead>
            <tr>
              <th>Test (today vs yesterday)</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            {DETECTION_RULES.map((rule) => (
              <tr key={rule.test}>
                <td>{rule.test}</td>
                <td className={`workflow-table__result workflow-table__result--${rule.kind}`}>{rule.result}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="workflow-callout">
          <strong>Leave reason:</strong> any leave reason means the member is leaving, so a Deletion
          is created. The only exception is &quot;Transfer In&quot;, a returning member who is never
          deleted. A future leave date waits until that date arrives.
        </div>

        <div className="workflow-callout workflow-callout--danger">
          <strong>Safety threshold:</strong> if more than <strong>99 members</strong> disappear from
          the list in one day, the whole submission is blocked and flagged as failed: no tickets are
          created and the file must be investigated. A PERSONID that appears more than once in one
          file is also rejected (a person must appear at most once per day).
        </div>
        <div className="workflow-callout">
          <strong>Checking Zone:</strong> a member flagged as both Addition and Deletion in the same
          run cancels out and is ignored.
        </div>
        <div className="workflow-callout">
          <strong>AZADEAID change (same country):</strong> when AZADEA gives a person a new AZADEAID
          while they stay in the same country, an <strong>EC ticket</strong> is created directly in
          List 2 so GLOBAL updates the AZADEAID (old to new). This is the only field change that
          creates a ticket.
        </div>
        <div className="workflow-callout">
          <strong>Transfers between countries:</strong> a transfer is not handled specially and the
          leave reason is not inspected. AZADEA sends it in two steps. First, the employee appears in
          the new country with a new PERSONID and a new AZADEAID and is removed from the old country,
          so the new identity is a normal addition (AD) and the old one a normal departure (DE).
          A few days later AZADEA restores the original AZADEAID on the new record, which is a
          same-country AZADEAID change and produces an EC. Because the AZADEAID settles back to the
          original and the PERSONID is not carried across the move, the List 3 insurance check is
          keyed on AZADEAID, not PERSONID.
        </div>
        <div className="workflow-callout">
          <strong>Planned:</strong> once PERSONID is reliably populated in List 3, the List 3 check
          can be revisited.
        </div>
      </section>

      <section className="workflow-card">
        <h2>Step 2: Members To Add</h2>
        <MembersToAddDiagram />
      </section>

      <section className="workflow-card">
        <h2>Step 3: Members To Delete</h2>
        <MembersToDeleteDiagram />
      </section>

      <section className="workflow-card">
        <h2>Step 4: Waiting tickets</h2>
        <p>
          Tickets blocked by an in-flight endorsement stay in <strong>List 1 only</strong> (status
          50 = AD waiting for a DE, status 12 = DE waiting for an AD), so GLOBAL never pulls them
          prematurely. A daily pass re-checks them: once the blocking ticket finishes, the waiting
          ticket is routed normally: clean to List 2, problems kept in List 1 with the error
          reason.
        </p>
      </section>

      <section className="workflow-card">
        <h2>Ticket lifecycle with GLOBAL</h2>
        <ol className="workflow-steps">
          {GLOBAL_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>
    </div>
  );
}
